// /docstudio/view/:id — 통합 문서 작성: 웹·앱 안에서 보기/편집.
// PC 프로그램 없이 브라우저(안드로이드 앱 WebView 포함)에서 바로 연다. 형식별 오픈소스 편집기:
//   워드·엑셀·파워포인트·PDF → ONLYOFFICE Docs(서버)      한글 HWP·HWPX → rhwp-studio(WebAssembly)
//   포토샵 PSD·이미지        → miniPaint + ag-psd           일러스트 AI·EPS·SVG → SVG-Edit(+LibreOffice 변환)
//   영상·음성               → 재생 + ffmpeg 자르기(서버)
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";
import { I } from "../../shared/Icon";
import { docsBase, viewPath } from "./base";

type DocFile = { id: string; name: string; ext: string; app: string; size: number; rev: number; updated_at: string };
type OpenInfo = { file: DocFile; kind: "office" | "hwp" | "image" | "vector" | "media" | "none"; raw_url: string; office_ready: boolean };

// 휴대폰 폭: 머리줄을 한 줄에 — '목록'·'버전' 글자는 숨기고 그림만, 상태 글자 숨김, 파일 이름은 남은 자리를 쓴다
const DV_CSS = `
@media (max-width:640px){
 .dv-head{padding:6px 8px!important;gap:6px!important}
 .dv-head .dv-txt,.dv-head [data-testid=save-status]{display:none}
 .dv-head .dv-name{font-size:13px!important}
 .dv-head [data-mode]{padding:6px 9px!important}
 .dv-head .t-btn{min-height:34px;padding:0 8px}
}`;
const PRETENDARD = "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, system-ui, 'Malgun Gothic', sans-serif";
const isMobile = () => window.matchMedia("(max-width: 820px)").matches || "ontouchstart" in window;
function errMsg(e: any) { return e?.response?.data?.detail ?? e?.message ?? "요청에 실패했습니다"; }

async function putContent(id: string, data: Blob | ArrayBuffer | Uint8Array) {
  const body = data instanceof Blob ? data : new Blob([data as BlobPart]);
  return (await api.put<DocFile>(`/docstudio/files/${id}/content`, body, { headers: { "Content-Type": "application/octet-stream" }, timeout: 0 })).data;
}

export default function DocViewerPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  // ?mode=view → 미리보기(보기 전용) · 없으면 편집
  const [sp, setSp] = useSearchParams();
  const mode: "view" | "edit" = sp.get("mode") === "view" ? "view" : "edit";
  const setMode = (m: "view" | "edit") => { setStatus(""); setSp(m === "view" ? { mode: "view" } : {}, { replace: true }); };
  const [info, setInfo] = useState<OpenInfo | null>(null);
  const [status, setStatus] = useState<string>("");
  const [showVersions, setShowVersions] = useState(false);
  const [reloadKey, setReloadKey] = useState(0); // 옛 판으로 되돌리면 편집기를 새로 띄운다

  useEffect(() => {
    api.get<OpenInfo>(`/docstudio/files/${id}/open`).then(r => setInfo(r.data))
      .catch(e => { toast.error("파일을 열 수 없습니다", errMsg(e)); nav(docsBase()); });
  }, [id, nav]);

  const onSaved = useCallback((f?: DocFile) => {
    setStatus(`저장됨 ${new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}`);
    if (f) setInfo(i => (i ? { ...i, file: { ...i.file, ...f } } : i));
  }, []);

  const download = async () => {
    if (!info) return;
    // 안드로이드 앱(WebView)은 blob: 주소를 못 받는다 → 서명 주소를 다운로드 관리자에 넘긴다
    if (/;\s*wv\)/i.test(navigator.userAgent)) { window.location.href = `${info.raw_url}&dl=1`; return; }
    const r = await api.get(`/docstudio/files/${id}/download`, { responseType: "blob", timeout: 0 });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement("a"); a.href = url; a.download = info.file.name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    // TIM_BOX 틀(t-main)에서는 높이 100%, 팀_문서 틀(세로 flex)에서는 남은 높이를 꽉 채운다 — 편집기가 화면에 딱 맞게.
    <div data-testid="docviewer" style={{ fontFamily: PRETENDARD, display: "flex", flexDirection: "column", flex: 1, height: "100%", overflow: "hidden", minHeight: docsBase() === "/teamdocs" ? 0 : "calc(100vh - 64px)" }}>
      <style>{DV_CSS}</style>
      <div className="dv-head" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid var(--border)", background: "var(--surface)" }}>
        <button className="t-btn t-btn-sm t-btn-ghost" onClick={() => nav(docsBase())} aria-label="목록으로" style={{ whiteSpace: "nowrap" }}><I.ChevL size={16}/><span className="dv-txt"> 목록</span></button>
        <div className="dv-name" style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{info?.file.name ?? "불러오는 중…"}</div>
        <span data-testid="save-status" style={{ fontSize: 12, color: "var(--text-3)", whiteSpace: "nowrap" }}>{status}</span>
        {info && info.kind !== "none" && (
          <div role="tablist" data-testid="mode-toggle" style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
            {(["view", "edit"] as const).map(m => (
              <button key={m} role="tab" aria-selected={mode === m} data-mode={m} onClick={() => setMode(m)}
                style={{ border: 0, padding: "5px 10px", fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
                  background: mode === m ? "var(--p-600)" : "transparent", color: mode === m ? "#fff" : "var(--text-2)" }}>
                {m === "view" ? "미리보기" : "편집"}
              </button>
            ))}
          </div>
        )}
        <button className="t-btn t-btn-sm" data-action="versions" onClick={() => setShowVersions(true)} title="버전 기록" style={{ whiteSpace: "nowrap" }}><I.Clock size={14}/><span className="dv-txt"> {info && info.file.rev > 1 ? `${info.file.rev}판` : "버전"}</span></button>
        <button className="t-btn t-btn-sm" onClick={download} title="내려받기"><I.Download size={14}/></button>
      </div>
      {showVersions && info && (
        <VersionsPanel fileId={id} onClose={() => setShowVersions(false)}
          onRestored={f => { setInfo(i => (i ? { ...i, file: { ...i.file, ...f } } : i)); setReloadKey(k => k + 1); setShowVersions(false); setStatus(`${f.rev}판으로 복원됨`); }}/>
      )}
      <div key={reloadKey} style={{ flex: 1, minHeight: 0, position: "relative", background: "var(--bg-1)" }}>
        {info && mode === "view" && <PreviewPane info={info} setStatus={setStatus}/>}
        {mode === "edit" && info?.kind === "office" && <OfficeEditor info={info} setStatus={setStatus}/>}
        {mode === "edit" && info?.kind === "hwp" && <HwpEditor info={info} onSaved={onSaved} setStatus={setStatus}/>}
        {mode === "edit" && info?.kind === "image" && <ImageEditor info={info} onSaved={onSaved} setStatus={setStatus}/>}
        {mode === "edit" && info?.kind === "vector" && <VectorEditor info={info} onSaved={onSaved} setStatus={setStatus}/>}
        {mode === "edit" && info?.kind === "media" && <MediaEditor info={info} setStatus={setStatus}/>}
        {mode === "edit" && info?.kind === "none" && (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-2)" }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>이 형식(.{info.file.ext})은 웹에서 열 수 있는 오픈소스 편집기가 없습니다.</div>
            <div style={{ fontSize: 13 }}>{info.file.ext === "prproj" ? "프리미어 프로젝트는 Adobe 전용 형식입니다. 안의 영상 파일을 올리면 웹에서 자르기·변환할 수 있습니다." : "내려받아 PC 프로그램으로 열어 주세요."}</div>
          </div>
        )}
      </div>
    </div>
  );
}

type Version = { rev: number; ext: string; name: string; size: number; saved_at: string };

function VersionsPanel({ fileId, onClose, onRestored }: { fileId: string; onClose: () => void; onRestored: (f: DocFile) => void }) {
  const [data, setData] = useState<{ current: DocFile; versions: Version[] } | null>(null);
  useEffect(() => { api.get(`/docstudio/files/${fileId}/versions`).then(r => setData(r.data)).catch(e => toast.error("버전을 불러오지 못했습니다", errMsg(e))); }, [fileId]);
  const fmt = (iso: string) => new Date(iso).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  const size = (n: number) => n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
  const dl = async (v: Version) => {
    const r = await api.get(`/docstudio/files/${fileId}/versions/${v.rev}/download`, { responseType: "blob", timeout: 0 });
    const url = URL.createObjectURL(r.data as Blob); const a = document.createElement("a");
    a.href = url; a.download = `${v.name.replace(/\.[^.]+$/, "")}_v${v.rev}.${v.ext}`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
  const restore = async (v: Version) => {
    if (!window.confirm(`${v.rev}판(${fmt(v.saved_at)})으로 되돌릴까요? 지금 내용도 버전으로 남습니다.`)) return;
    try { onRestored((await api.post<DocFile>(`/docstudio/files/${fileId}/versions/${v.rev}/restore`)).data); toast.success(`${v.rev}판으로 되돌렸습니다`); }
    catch (e) { toast.error("되돌리지 못했습니다", errMsg(e)); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} data-testid="versions-panel" style={{ width: "min(380px, 100%)", height: "100%", background: "var(--surface)", padding: 16, overflow: "auto", boxShadow: "var(--shadow-md)" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontWeight: 800, fontSize: 16, flex: 1 }}>버전 기록</div>
          <button className="t-btn t-btn-sm t-btn-ghost" onClick={onClose}><I.X size={14}/></button>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 12 }}>저장할 때마다 서버에 이전 판이 남습니다(최근 20판). 어느 기기에서 고쳤든 여기서 되돌릴 수 있습니다.</div>
        {data && (
          <div style={{ display: "grid", gap: 8 }}>
            <div className="t-card" style={{ padding: 10, display: "block", border: "2px solid var(--p-600)" }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{data.current.rev}판 · 현재</div>
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>{fmt(data.current.updated_at)} · {size(data.current.size)}</div>
            </div>
            {data.versions.length === 0 && <div style={{ fontSize: 13, color: "var(--text-3)" }}>아직 이전 판이 없습니다.</div>}
            {data.versions.map(v => (
              <div key={v.rev} data-version={v.rev} className="t-card" style={{ padding: 10, display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{v.rev}판{v.ext !== data.current.ext ? ` (.${v.ext})` : ""}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>{fmt(v.saved_at)} · {size(v.size)}</div>
                </div>
                <button className="t-btn t-btn-sm" onClick={() => dl(v)} title="이 판 내려받기"><I.Download size={14}/></button>
                <button className="t-btn t-btn-sm" data-action="restore" onClick={() => restore(v)}>되돌리기</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Busy = ({ text }: { text: string }) => (
  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)", fontSize: 13, pointerEvents: "none" }}>{text}</div>
);

function SaveBar({ children }: { children: React.ReactNode }) {
  return <div style={{ position: "absolute", right: 16, bottom: 64, zIndex: 5, display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>{children}</div>;
}

// ── 미리보기(보기 전용) ───────────────────────────────────────────────────
// 편집기보다 가볍고 빨리 열린다. 문서류는 ONLYOFFICE 보기 모드(휴대폰이면 모바일 보기),
// 그림은 그림(PSD 는 서버가 PNG 로), 영상·음성은 재생만.
function PreviewPane({ info, setStatus }: { info: OpenInfo; setStatus: (s: string) => void }) {
  const [pv, setPv] = useState<{ viewer: string; url?: string; config?: any; api_js?: string } | null>(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  const [zoom, setZoom] = useState(false);
  const holder = `pv-${info.file.id}`;
  useEffect(() => {
    let alive = true, ed: any = null;
    setStatus("미리보기 (보기 전용)");
    (async () => {
      try {
        const phone = window.matchMedia("(max-width: 820px)").matches;
        const { data } = await api.get(`/docstudio/files/${info.file.id}/preview`, { params: { mobile: phone }, timeout: 0 });
        if (!alive) return;
        setPv(data);
        if (data.viewer === "office") {
          await loadScript(data.api_js);
          if (!alive) return;
          ed = new (window as any).DocsAPI.DocEditor(holder, {
            ...data.config,
            events: {
              onAppReady: () => setReady(true), onDocumentReady: () => setReady(true),
              onError: (e: any) => setErr(`보기 오류 ${e?.data?.errorCode ?? ""} ${e?.data?.errorDescription ?? ""}`),
            },
          });
        } else setReady(true);
      } catch (e) { setErr(errMsg(e)); }
    })();
    return () => { alive = false; try { ed?.destroyEditor(); } catch { /* noop */ } };
  }, [info.file.id, info.file.rev, holder, setStatus]);

  return (
    <div data-testid="preview-pane" style={{ position: "absolute", inset: 0, overflow: pv?.viewer === "image" ? "auto" : "hidden", background: pv?.viewer === "image" ? "#1f2330" : undefined }}>
      {err && <div style={{ padding: 24, color: "var(--danger)" }}>{err}</div>}
      {!ready && !err && <Busy text="미리보기를 여는 중…"/>}
      {/* 보기 모듈이 붙을 자리 — 서버 응답보다 먼저 있어야 한다(스크립트가 이미 받아져 있으면 바로 붙는다) */}
      <div style={{ position: "absolute", inset: 0, display: pv && pv.viewer !== "office" ? "none" : "block" }}><div id={holder}/></div>
      {pv?.viewer === "image" && pv.url && (
        <img src={pv.url} alt={info.file.name} onClick={() => setZoom(z => !z)} data-testid="preview-image"
          style={zoom ? { display: "block", maxWidth: "none", cursor: "zoom-out" }
            : { display: "block", maxWidth: "100%", maxHeight: "100%", margin: "auto", position: "absolute", inset: 0, objectFit: "contain", cursor: "zoom-in" }}/>
      )}
      {pv?.viewer === "video" && pv.url && <video src={pv.url} controls playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "#000" }}/>}
      {pv?.viewer === "audio" && pv.url && <div style={{ padding: 24 }}><audio src={pv.url} controls style={{ width: "100%" }}/></div>}
      {pv?.viewer === "none" && (
        <div style={{ padding: 32, textAlign: "center", color: "var(--text-2)" }}>
          이 형식(.{info.file.ext})은 미리보기가 없습니다. 내려받아 PC 프로그램으로 열어 주세요.
        </div>
      )}
    </div>
  );
}

// ── 워드·엑셀·파워포인트·PDF: ONLYOFFICE ─────────────────────────────────
let ooScript: Promise<void> | null = null;
function loadScript(src: string) {
  if (!ooScript) {
    ooScript = new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = src; s.onload = () => res(); s.onerror = () => { ooScript = null; rej(new Error("문서 편집 서버에 연결하지 못했습니다")); };
      document.head.appendChild(s);
    });
  }
  return ooScript;
}

function OfficeEditor({ info, setStatus }: { info: OpenInfo; setStatus: (s: string) => void }) {
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);
  const holder = `oo-${info.file.id}`;
  useEffect(() => {
    let ed: any = null, alive = true;
    (async () => {
      try {
        const { data } = await api.get<{ config: any; api_js: string }>(`/docstudio/files/${info.file.id}/office-config`, { params: { mobile: isMobile() } });
        await loadScript(data.api_js);
        if (!alive) return;
        const cfg = {
          ...data.config,
          events: {
            onAppReady: () => setReady(true),       // PDF 편집기는 onDocumentReady 를 늦게(또는 안) 보낸다
            onDocumentReady: () => setReady(true),
            onDocumentStateChange: (e: any) => setStatus(e.data ? "편집 중…" : "저장됨 (자동)"),
            onError: (e: any) => setErr(`편집기 오류 ${e?.data?.errorCode ?? ""} ${e?.data?.errorDescription ?? ""}`),
          },
        };
        ed = new (window as any).DocsAPI.DocEditor(holder, cfg);
      } catch (e) { setErr(errMsg(e)); }
    })();
    return () => { alive = false; try { ed?.destroyEditor(); } catch { /* noop */ } };
  }, [info.file.id, holder, setStatus]);
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {err ? <div style={{ padding: 24, color: "var(--danger)" }}>{err}</div> : !ready && <Busy text="문서 편집기를 여는 중…"/>}
      <div id={holder} style={{ position: "absolute", inset: 0 }}/>
    </div>
  );
}

// ── 한글 HWP·HWPX: rhwp-studio ──────────────────────────────────────────
function HwpEditor({ info, onSaved, setStatus }: { info: OpenInfo; onSaved: (f?: DocFile) => void; setStatus: (s: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const ed = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { createEditor } = await import("@rhwp/editor");
        const editor = await createEditor(box.current!, { studioUrl: `${window.location.origin}/rhwp/index.html` });
        if (!alive) { editor.destroy(); return; }
        ed.current = editor;
        const buf = await (await fetch(info.raw_url)).arrayBuffer();
        const r = await editor.loadFile(buf, info.file.name);
        setReady(true);
        setStatus(`${r.pageCount}쪽`);
        editor.onDocumentChanged?.(() => setStatus("편집 중… (저장을 눌러 주세요)"));
      } catch (e) { setErr(errMsg(e)); }
    })();
    return () => { alive = false; try { ed.current?.destroy(); } catch { /* noop */ } };
  }, [info.file.id, info.raw_url, info.file.name, setStatus]);

  const save = useCallback(async () => {
    if (!ed.current) return;
    setBusy(true);
    try {
      const bytes: Uint8Array = info.file.ext === "hwpx" ? await ed.current.exportHwpx() : await ed.current.exportHwp();
      const f = await putContent(info.file.id, bytes);
      await ed.current.notifySaved?.();
      onSaved(f); toast.success("한글 문서를 저장했습니다");
    } catch (e) { toast.error("저장 실패", errMsg(e)); }
    finally { setBusy(false); }
  }, [info.file.id, info.file.ext, onSaved]);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {!ready && !err && <Busy text="한글 편집기를 여는 중…"/>}
      {err && <div style={{ padding: 24, color: "var(--danger)" }}>{err}</div>}
      <div ref={box} data-testid="hwp-editor" style={{ position: "absolute", inset: 0 }}/>
      {ready && <SaveBar><button className="t-btn t-btn-primary" data-action="save" onClick={save} disabled={busy}>{busy ? "저장 중…" : "팀박스에 저장"}</button></SaveBar>}
    </div>
  );
}

// ── 포토샵 PSD·이미지: miniPaint (+ ag-psd 로 레이어 읽기/쓰기) ─────────────
const BROWSER_IMG = new Set(["jpg", "jpeg", "png", "gif", "bmp", "webp"]);
const MIME: Record<string, string> = { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", bmp: "image/bmp", gif: "image/png" };

function ImageEditor({ info, onSaved, setStatus }: { info: OpenInfo; onSaved: (f?: DocFile) => void; setStatus: (s: string) => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ext = info.file.ext;
  const canEdit = ext === "psd" || BROWSER_IMG.has(ext);

  const mp = () => frame.current?.contentWindow as any;

  useEffect(() => {
    if (!canEdit) return;
    let alive = true;
    const iframe = frame.current!;
    const onLoad = async () => {
      try {
        for (let i = 0; i < 100 && !mp()?.FileOpen; i++) await new Promise(r => setTimeout(r, 100));
        const w = mp();
        if (!w?.FileOpen) throw new Error("그림 편집기를 불러오지 못했습니다");
        if (ext === "psd") {
          const { readPsd } = await import("ag-psd");
          const buf = await (await fetch(info.raw_url)).arrayBuffer();
          const psd = readPsd(buf, { skipThumbnail: true });
          const blank = JSON.parse(w.FileSave.export_as_json());
          const tpl = blank.layers[0];
          const flat: any[] = [];
          const walk = (ls: any[] = [], hidden = false) => ls.forEach(l => l.children ? walk(l.children, hidden || l.hidden) : l.canvas && flat.push({ ...l, hidden: hidden || l.hidden }));
          walk(psd.children);
          if (!flat.length && psd.canvas) flat.push({ name: "배경", canvas: psd.canvas, left: 0, top: 0, opacity: 1 });
          const layers = flat.map((l, i) => ({
            ...tpl, id: i + 1, name: l.name || `레이어 ${i + 1}`, type: "image", order: i + 1,
            x: l.left || 0, y: l.top || 0, width: l.canvas.width, height: l.canvas.height,
            width_original: l.canvas.width, height_original: l.canvas.height,
            visible: !l.hidden, opacity: Math.round((l.opacity ?? 1) * 100), data: null, link: null, params: {}, filters: [],
          }));
          const data = flat.map((l, i) => ({ id: i + 1, data: l.canvas.toDataURL("image/png") }));
          await w.FileOpen.load_json({ ...blank, info: { ...blank.info, width: psd.width, height: psd.height, layer_active: layers.length }, layers, data });
          setStatus(`레이어 ${layers.length}개`);
        } else {
          w.FileOpen.open_resource(info.raw_url);
        }
        if (alive) setReady(true);
      } catch (e) { setErr(errMsg(e)); }
    };
    iframe.addEventListener("load", onLoad);
    return () => { alive = false; iframe.removeEventListener("load", onLoad); };
  }, [info.raw_url, ext, canEdit, setStatus]);

  const save = async () => {
    const w = mp();
    if (!w?.Layers) return;
    setBusy(true);
    try {
      const dim = w.Layers.get_dimensions();
      const merged = document.createElement("canvas");
      merged.width = dim.width; merged.height = dim.height;
      w.Layers.convert_layers_to_canvas(merged.getContext("2d"), null, false);
      let f: DocFile;
      if (ext === "psd") {
        const { writePsd } = await import("ag-psd");
        const children = (w.AppConfig.layers as any[]).map(l => {
          const c = document.createElement("canvas");
          c.width = dim.width; c.height = dim.height;
          w.Layers.convert_layers_to_canvas(c.getContext("2d"), l.id, false);
          return { name: l.name, canvas: c, left: 0, top: 0, hidden: !l.visible, opacity: (l.opacity ?? 100) / 100 };
        });
        const out = writePsd({ width: dim.width, height: dim.height, canvas: merged, children }, { generateThumbnail: true });
        f = await putContent(info.file.id, out);
      } else {
        const blob = await new Promise<Blob>((res, rej) => merged.toBlob(b => b ? res(b) : rej(new Error("그림을 만들지 못했습니다")), MIME[ext] || "image/png", 0.92));
        if (ext === "gif") {
          const name = info.file.name.replace(/\.[^.]+$/, "") + "_편집.png";
          await api.post(`/docstudio/files/${info.file.id}/save-as`, blob, { params: { name }, headers: { "Content-Type": "application/octet-stream" } });
          toast.success("편집본을 PNG 새 파일로 저장했습니다"); setBusy(false); return;
        }
        f = await putContent(info.file.id, blob);
      }
      onSaved(f); toast.success(ext === "psd" ? "PSD(레이어 포함)로 저장했습니다" : "그림을 저장했습니다");
    } catch (e) { toast.error("저장 실패", errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!canEdit) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "var(--text-2)" }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>.{ext} 는 브라우저가 직접 그릴 수 없는 형식입니다.</div>
        <div style={{ fontSize: 13 }}>{ext === "psb" ? "대용량 PSB 는 지원하지 않습니다. PSD 로 저장해 올려 주세요." : "PNG·JPG·PSD 로 바꿔 올리면 웹에서 편집할 수 있습니다."}</div>
      </div>
    );
  }
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {!ready && !err && <Busy text="그림 편집기를 여는 중…"/>}
      {err && <div style={{ padding: 24, color: "var(--danger)" }}>{err}</div>}
      <iframe ref={frame} title="그림 편집기" data-testid="image-editor" src="/vendor/minipaint/index.html" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}/>
      {ready && <SaveBar><button className="t-btn t-btn-primary" data-action="save" onClick={save} disabled={busy}>{busy ? "저장 중…" : "팀박스에 저장"}</button></SaveBar>}
    </div>
  );
}

// ── 일러스트 AI·EPS·SVG: SVG-Edit ─────────────────────────────────────────
function VectorEditor({ info, onSaved, setStatus }: { info: OpenInfo; onSaved: (f?: DocFile) => void; setStatus: (s: string) => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    const iframe = frame.current!;
    const onLoad = async () => {
      try {
        const svg = (await api.get(`/docstudio/files/${info.file.id}/to-svg`, { responseType: "text", timeout: 0 })).data as string;
        const w = iframe.contentWindow as any;
        for (let i = 0; i < 100 && !w?.svgEditor?.svgCanvas; i++) await new Promise(r => setTimeout(r, 100));
        if (!w?.svgEditor?.svgCanvas) throw new Error("벡터 편집기를 불러오지 못했습니다");
        await w.svgEditor.loadFromString(svg, { noAlert: true });
        try { w.svgEditor.zoomChanged(w, "canvas"); } catch { /* 화면 맞춤 실패해도 편집은 된다 */ }
        if (alive) { setReady(true); setStatus(info.file.ext === "svg" ? "" : "AI·EPS 는 SVG 로 바꿔 열었습니다"); }
      } catch (e) { setErr(errMsg(e)); }
    };
    iframe.addEventListener("load", onLoad);
    return () => { alive = false; iframe.removeEventListener("load", onLoad); };
  }, [info.file.id, info.file.ext, setStatus]);

  const save = async () => {
    const w = frame.current?.contentWindow as any;
    if (!w?.svgEditor) return;
    setBusy(true);
    try {
      const svg = w.svgEditor.svgCanvas.getSvgString();
      const f = (await api.post<DocFile>(`/docstudio/files/${info.file.id}/save-vector`, { svg }, { timeout: 0 })).data;
      if (f.id === info.file.id) { onSaved(f); toast.success(info.file.ext === "ai" ? "AI(PDF 호환)로 저장했습니다" : "저장했습니다"); }
      else toast.success(`새 파일로 저장했습니다: ${f.name}`);
    } catch (e) { toast.error("저장 실패", errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {!ready && !err && <Busy text="벡터 편집기를 여는 중…"/>}
      {err && <div style={{ padding: 24, color: "var(--danger)" }}>{err}</div>}
      <iframe ref={frame} title="벡터 편집기" data-testid="vector-editor" src="/vendor/svgedit/timbox.html" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}/>
      {ready && <SaveBar><button className="t-btn t-btn-primary" data-action="save" onClick={save} disabled={busy}>{busy ? "저장 중…" : "팀박스에 저장"}</button></SaveBar>}
    </div>
  );
}

// ── 영상·음성: 재생 + 자르기(ffmpeg) ──────────────────────────────────────
function MediaEditor({ info, setStatus }: { info: OpenInfo; setStatus: (s: string) => void }) {
  const nav = useNavigate();
  const vid = useRef<HTMLVideoElement>(null);
  const [meta, setMeta] = useState<{ duration: number; width?: number; height?: number; playable: boolean } | null>(null);
  const [range, setRange] = useState<[number, number]>([0, 0]);
  const [mute, setMute] = useState(false);
  const [output, setOutput] = useState("mp4");
  const [busy, setBusy] = useState(false);
  const isAudio = ["mp3", "wav", "aac", "m4a"].includes(info.file.ext);

  useEffect(() => {
    api.get(`/docstudio/files/${info.file.id}/media-info`).then(r => {
      setMeta(r.data); setRange([0, Math.round(r.data.duration * 10) / 10]);
      setStatus(r.data.width ? `${r.data.width}×${r.data.height} · ${r.data.duration.toFixed(1)}초` : `${r.data.duration.toFixed(1)}초`);
    }).catch(() => {});
    if (isAudio) setOutput("mp3");
  }, [info.file.id, isAudio, setStatus]);

  const here = (i: 0 | 1) => { const t = Math.round((vid.current?.currentTime ?? 0) * 10) / 10; setRange(r => (i === 0 ? [t, Math.max(t + 0.1, r[1])] : [r[0], Math.max(r[0] + 0.1, t)])); };

  const run = async () => {
    setBusy(true);
    try {
      const f = (await api.post<DocFile>(`/docstudio/files/${info.file.id}/media-edit`, { start: range[0], end: range[1], mute, output }, { timeout: 0 })).data;
      toast.success(`새 파일로 만들었습니다: ${f.name}`);
      nav(viewPath(f.id));
    } catch (e) { toast.error("편집 실패", errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
      {isAudio
        ? <audio src={info.raw_url} controls style={{ width: "min(640px, 100%)", marginTop: 24 }}/>
        : <video ref={vid} data-testid="media-player" src={info.raw_url} controls playsInline style={{ width: "min(960px, 100%)", maxHeight: "60vh", background: "#000", borderRadius: 8 }}/>}
      {meta && !meta.playable && <div style={{ fontSize: 12, color: "var(--warn)" }}>이 코덱은 브라우저에서 재생되지 않을 수 있습니다. 아래에서 MP4 로 바꾸면 어디서나 재생됩니다.</div>}
      <div className="t-card" style={{ width: "min(960px, 100%)", padding: 14, display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>자르기 · 변환</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
          시작 <input className="t-input" type="number" step="0.1" min={0} value={range[0]} onChange={e => setRange([+e.target.value, range[1]])} style={{ width: 90 }}/>
          {!isAudio && <button className="t-btn t-btn-sm" onClick={() => here(0)}>현재 위치</button>}
          끝 <input className="t-input" type="number" step="0.1" min={0} value={range[1]} onChange={e => setRange([range[0], +e.target.value])} style={{ width: 90 }}/>
          {!isAudio && <button className="t-btn t-btn-sm" onClick={() => here(1)}>현재 위치</button>}
          <span style={{ color: "var(--text-3)" }}>/ 전체 {meta?.duration.toFixed(1) ?? "-"}초</span>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
          {!isAudio && <label style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={mute} onChange={e => setMute(e.target.checked)}/> 소리 빼기</label>}
          <select className="t-input" value={output} onChange={e => setOutput(e.target.value)} style={{ width: 160 }}>
            {!isAudio && <option value="mp4">MP4 (어디서나 재생)</option>}
            {!isAudio && <option value="webm">WebM</option>}
            {!isAudio && <option value="gif">GIF (움짤)</option>}
            <option value="mp3">MP3 (소리만)</option>
          </select>
          <button className="t-btn t-btn-primary" data-action="media-run" onClick={run} disabled={busy || !meta || range[1] <= range[0]}>{busy ? "만드는 중…" : "새 파일로 만들기"}</button>
        </div>
      </div>
    </div>
  );
}
